import { z } from "zod";
import { agentMasterWashSchema, agentWashActivationPayloadSchema, agentWashCreditIssuedPayloadSchema, agentWashCyclePayloadSchema, agentWashStartPayloadSchema, agentWashUnitStatusPayloadSchema, } from "../wash/protocol.js";
import { localDateSchema, moneySchema, nonNegativeMoneySchema, quantitySchema, rateSchema, timestampSchema, unitPriceSchema, uuidSchema, } from "../common/primitives.js";
import { fuelPaymentTypeSchema, millimetreSchema, temperatureSchema } from "../fuel/readings.js";
import { agentMasterOpenShiftSchema, agentMasterPosSettingsSchema, agentMasterPosTerminalSchema, agentMasterStaffSchema, agentPosSalePayloadSchema, agentPosSaleRetenderPayloadSchema, agentPosSaleVoidPayloadSchema, } from "../pos/protocol.js";
import { campaignSchema, fleetPolicySchema, fleetUsageSchema, loyaltyEarnRuleSchema, loyaltyPointValueSchema, subscriptionEntitlementSchema, } from "@station/pricing-engine";
import { agentAdapterKindSchema, agentAdapterStatusSchema, agentAlarmSeveritySchema, agentArchSchema, agentCommandTypeSchema, agentDeviceStatusSchema, agentNozzleStatusSchema, agentOsSchema, agentSyncRejectionCodeSchema, agentUpdateChannelSchema, } from "./enums.js";
/**
 * İstasyon ajanı ↔ bulut protokolü v1 — `docs/domain/agent-protocol.md`'nin makine okunur karşılığı.
 *
 * **Bu dosya Go ajanıyla ORTAK sözleşmedir**: yol, alan adı, durum kodu ve imza bayt biçimi belgedeki gibidir.
 * Değişmezler:
 *  1. Zarf `type: string` + `payload: unknown`'dur — AYRIK BİRLEŞİM DEĞİL. Bilinmeyen tür partiyi DÜŞÜRMEZ,
 *     yalnızca o kayıt `UNSUPPORTED_EVENT_TYPE` ile reddedilir (eski sunucu yeni ajanı düşürmez).
 *  2. Yük şemaları tür başına AYRI tanımlıdır ve kayıt kayıt doğrulanır.
 *  3. Para kuruş tam sayı DİZGE, litre ≤ 3 ondalık DİZGE, birim fiyat ≤ 4 ondalık DİZGE (akaryakıt sözleşmesiyle aynı).
 */
/** Bir senkron partisindeki en çok olay sayısı (protokol §3). */
export const AGENT_SYNC_MAX_EVENTS = 2000;
/** Long-poll üst sınırı (saniye). */
export const AGENT_COMMAND_MAX_WAIT_SECONDS = 30;
/** Komut sorgusunda bir seferde alınabilecek en çok komut. */
export const AGENT_COMMAND_MAX_LIMIT = 50;
/** İmza / heartbeat saat penceresi (saniye): |sunucu saati − timestamp| bunu aşarsa `AGENT_CLOCK_SKEW`. */
export const AGENT_CLOCK_WINDOW_SECONDS = 300;
/** Nonce'ın yeniden kullanılamayacağı süre (saniye). */
export const AGENT_NONCE_TTL_SECONDS = 600;
/** Token isteğinde imzalanan bayt dizisinin ön eki (protokol §1.2). */
export const AGENT_TOKEN_SIGNATURE_PREFIX = "station-agent-token";
// ── Ortak ilkel şemalar ──────────────────────────────────────────────────────
/** base64 kodlu 32 bayt (ed25519 açık anahtarı). */
export const agentPublicKeySchema = z
    .string()
    .regex(/^[A-Za-z0-9+/]{43}=$/, "Açık anahtar base64 kodlu 32 bayt olmalıdır")
    .describe("ed25519 açık anahtarı (base64, 32 bayt)");
/** base64 kodlu 64 bayt (ed25519 imzası). */
export const agentSignatureSchema = z
    .string()
    .regex(/^[A-Za-z0-9+/]{86}==$/, "İmza base64 kodlu 64 bayt olmalıdır")
    .describe("ed25519 imzası (base64, 64 bayt)");
/** base64 kodlu 16 bayt (tekrar koruması). */
export const agentNonceSchema = z
    .string()
    .regex(/^[A-Za-z0-9+/]{22}==$/, "Nonce base64 kodlu 16 bayt olmalıdır")
    .describe("Tek kullanımlık rastgele değer (base64, 16 bayt)");
/**
 * Kayıt kodu — Crockford base32, `XXXX-XXXX-XXXX` (60 bit). Ajan boşluk / tire / küçük harfi normalleştirir;
 * sunucu da aynı normalleştirmeyi uygular, bu yüzden şema GEVŞEKTİR (uzunluk + karakter kümesi).
 */
export const agentEnrollmentCodeInputSchema = z
    .string()
    .trim()
    .min(12)
    .max(20)
    .describe("Kayıt kodu (Crockford base32, XXXX-XXXX-XXXX)");
export const agentVersionSchema = z
    .string()
    .trim()
    .min(1)
    .max(40)
    .describe("Ajan sürümü (semver; geliştirmede '0.0.0-dev' olabilir)");
// ── §1.1 Kayıt ───────────────────────────────────────────────────────────────
export const agentEnrollRequestSchema = z.object({
    code: agentEnrollmentCodeInputSchema,
    publicKey: agentPublicKeySchema,
    hostname: z.string().trim().min(1).max(120),
    os: agentOsSchema,
    arch: agentArchSchema,
    version: agentVersionSchema,
});
export const agentEnrollResponseSchema = z.object({
    agentId: uuidSchema,
    tenantId: uuidSchema,
    stationId: uuidSchema,
    stationCode: z.string(),
    stationName: z.string(),
    serverTime: timestampSchema,
    tokenTtlSeconds: z.number().int().positive(),
    heartbeatIntervalSeconds: z.number().int().positive(),
});
// ── §1.2 Token ───────────────────────────────────────────────────────────────
export const agentTokenRequestSchema = z.object({
    agentId: uuidSchema,
    timestamp: timestampSchema,
    nonce: agentNonceSchema,
    signature: agentSignatureSchema,
});
export const agentTokenResponseSchema = z.object({
    accessToken: z.string(),
    expiresInSeconds: z.number().int().positive(),
    serverTime: timestampSchema,
});
/**
 * Token isteğinde imzalanan bayt dizisi (UTF-8, satır sonu `\n`, SONDA `\n` YOK):
 *   "station-agent-token\n" + agentId + "\n" + timestamp + "\n" + nonce
 * Saf fonksiyon: Go tarafı da bunu birebir üretir (`agent/internal/identity`).
 */
export function agentTokenSignatureMessage(input) {
    return `${AGENT_TOKEN_SIGNATURE_PREFIX}\n${input.agentId}\n${input.timestamp}\n${input.nonce}`;
}
// ── §2 Ana veri ──────────────────────────────────────────────────────────────
export const agentStrappingRowSchema = z.object({
    mm: z.string().describe("Yükseklik (mm, ≤ 2 ondalık dizge)"),
    litres: quantitySchema,
});
/**
 * Ana veri ürünü. Prompt 14'te yalnızca tank / tabancaya bağlı akaryakıt ürünleri vardı; **Prompt 15'ten
 * itibaren şirketin TÜM etkin ürünleri** (market, yıkama, yağ…) fiyatlarıyla iner — yerel kasa çevrimdışı
 * satış yapabilsin diye. POS alanları İSTEĞE BAĞLIDIR: eski ajan onları yok sayar (protokol §8).
 */
export const agentMasterProductSchema = z.object({
    id: uuidSchema,
    code: z.string(),
    name: z.string(),
    unit: z.string(),
    productType: z.string().optional(),
    businessUnit: z.string().optional(),
    accountingGroup: z.string().optional(),
    vatRateCode: z.string().optional(),
    vatRatePercent: rateSchema.nullable().optional().describe("Bugün geçerli KDV yüzdesi"),
    /** Market / hizmet ürününün KURUŞ birim fiyatı; akaryakıtta null (bkz. `unitPricePerLitre`). */
    unitPrice: nonNegativeMoneySchema.nullable().optional(),
    /** Akaryakıt / LPG ürününün ₺/L fiyatı (4 ondalık); diğer ürünlerde null. */
    unitPricePerLitre: unitPriceSchema.nullable().optional(),
    barcodes: z.array(z.string()).optional(),
    isAgeRestricted: z.boolean().optional(),
    quickKeySlot: z.number().int().nullable().optional(),
    categoryId: uuidSchema.nullable().optional(),
});
export const agentMasterTankSchema = z.object({
    id: uuidSchema,
    tankNo: z.number().int(),
    productId: uuidSchema,
    capacityL: quantitySchema,
    heightMm: z.string().nullable().describe("Kalibrasyon tablosunun azami yüksekliği (mm)"),
    lastLevelMm: z.string().nullable(),
    lastMeasuredAt: timestampSchema.nullable(),
    /** Simülatör ve yerel POS seviye ↔ hacim dönüşümü için kullanır; kalibrasyon tablosu yoksa boş dizi. */
    strapping: z.array(agentStrappingRowSchema),
});
export const agentMasterNozzleSchema = z.object({
    id: uuidSchema,
    nozzleNo: z.number().int(),
    tankId: uuidSchema,
    productId: uuidSchema,
    totalizerDigits: z.number().int(),
    currentTotalizerL: quantitySchema,
    /** Tabancanın ürününün istasyondaki YÜRÜRLÜKTEKİ fiyatı; fiyat girilmemişse null. */
    unitPrice: unitPriceSchema.nullable(),
});
export const agentMasterDispenserSchema = z.object({
    id: uuidSchema,
    dispenserNo: z.number().int(),
    islandNo: z.number().int().nullable(),
    nozzles: z.array(agentMasterNozzleSchema),
});
export const agentMasterFiscalDeviceSchema = z.object({
    id: uuidSchema,
    serialNo: z.string(),
    scope: z.string(),
    lastZNo: z.number().int().nullable(),
});
/** Yerel POS (Prompt 15) içindir; KİŞİSEL VERİ taşımaz (unvan dışında ad / adres / telefon yoktur). */
export const agentMasterCounterpartyLimitSchema = z.object({
    counterpartyId: uuidSchema,
    code: z.string(),
    name: z.string(),
    availableCredit: moneySchema.nullable().describe("null = limitsiz"),
    blocked: z.boolean(),
});
export const agentConfigSchema = z.object({
    heartbeatIntervalSeconds: z.number().int().positive(),
    syncBatchSize: z.number().int().positive(),
    syncIntervalSeconds: z.number().int().positive(),
    totalizerReadingIntervalSeconds: z.number().int().positive(),
    tankReadingIntervalSeconds: z.number().int().positive(),
    updateChannel: agentUpdateChannelSchema,
});
/** Ajan başına yapılandırma geçersiz kılması (panel / `UPDATE_CONFIG`). Verilmeyen alan varsayılanda kalır. */
export const agentConfigOverrideSchema = agentConfigSchema.partial();
// ── Prompt 17 (müşteri, filo / TTS, sadakat — ADR 0017 §29, agent-protocol §2) ─────────────────
// KİŞİSEL VERİ: istasyona ad / telefon / e-posta GİTMEZ (maskeli ad). Plaka yalnızca FİLO aracında açık gider (kurumsal
// varlık); bireysel araç için yalnızca `sha256("plate:" + normalize(plaka))` özeti (`plateHashes`, `plateHash`).
const pointsBalanceSchema = z
    .string()
    .regex(/^-?\d{1,15}$/)
    .describe("Tam sayı puan; iade yüzünden eksi olabilir");
const sha256HexSchema = z.string().regex(/^[0-9a-f]{64}$/);
export const agentMasterLoyaltyProgramSchema = z.object({
    programName: z.string(),
    pointValue: loyaltyPointValueSchema.describe("₺ / puan, 4 ondalık"),
    earnEnabled: z.boolean(),
    redeemEnabled: z.boolean(),
    minRedeemPoints: z.number().int().nonnegative(),
    /** Boş = tüm iş birimleri. */
    redeemableBusinessUnits: z.array(z.string()),
    excludeAgeRestricted: z.boolean(),
    tiers: z.array(z.object({
        code: z.string(),
        name: z.string(),
        rank: z.number().int(),
        earnMultiplier: z.string().regex(/^\d{1,3}(\.\d{1,3})?$/),
    })),
    /** POS yalnızca GÖSTERİR (kazanılacak puan); gerçek kazanımı sunucu hesaplar. */
    earnRules: z.array(loyaltyEarnRuleSchema),
});
export const agentMasterLoyaltyAccountSchema = z.object({
    customerProfileId: uuidSchema,
    cardNo: z.string().min(1).max(32),
    maskedName: z.string().max(80),
    tier: z.string().nullable(),
    pointsBalance: pointsBalanceSchema,
    redeemBlocked: z.boolean(),
    plateHashes: z.array(sha256HexSchema).max(20),
});
export const agentMasterCouponSchema = z.object({
    couponId: uuidSchema,
    code: z.string().min(1).max(40),
    campaignId: uuidSchema,
    customerProfileId: uuidSchema.nullable(),
    validFrom: timestampSchema,
    validUntil: timestampSchema.nullable(),
    remainingUses: z.number().int().positive(),
});
export const agentMasterSubscriptionSchema = subscriptionEntitlementSchema.extend({
    planName: z.string(),
    customerProfileId: uuidSchema.nullable(),
    counterpartyId: uuidSchema.nullable(),
    plateHash: sha256HexSchema.nullable(),
});
export const agentMasterFleetVehicleSchema = z.object({
    vehicleId: uuidSchema,
    counterpartyId: uuidSchema,
    counterpartyName: z.string(),
    /** Normalize plaka (büyük harf, boşluksuz). KVKK: filo aracı plakası (kurumsal varlık). */
    plate: z.string().max(20),
    tagIds: z.array(z.string().min(1).max(64)).max(20),
    /** ETKİN kural (cari varsayılanı + araç geçersiz kılması birleşmiş). */
    policy: fleetPolicySchema,
    /** Kullanım anlık görüntüsü (filo kullanım defterinden); hiç kullanım yoksa `null`. */
    usage: fleetUsageSchema.nullable(),
});
export const agentMasterFleetDefaultPolicySchema = z.object({
    counterpartyId: uuidSchema,
    policy: fleetPolicySchema,
    usage: fleetUsageSchema.nullable(),
});
export const agentMasterFleetDriverSchema = z.object({
    fleetDriverId: uuidSchema,
    counterpartyId: uuidSchema,
    cardNo: z.string().min(1).max(40),
    maskedName: z.string().max(80),
});
export const agentMasterPrivacySchema = z.object({
    vehicleReadRetentionDays: z.number().int().min(1).max(3650),
});
export const agentMasterDataSchema = z.object({
    revision: z.string().describe("İçerik özeti; `If-None-Match` ile 304 döner"),
    generatedAt: timestampSchema,
    station: z.object({
        id: uuidSchema,
        code: z.string(),
        name: z.string(),
        timezone: z.string(),
    }),
    products: z.array(agentMasterProductSchema),
    tanks: z.array(agentMasterTankSchema),
    dispensers: z.array(agentMasterDispenserSchema),
    fiscalDevices: z.array(agentMasterFiscalDeviceSchema),
    counterpartyLimits: z.array(agentMasterCounterpartyLimitSchema),
    config: agentConfigSchema,
    // ── Prompt 15 (yerel kasa) genişletmesi — hepsi İSTEĞE BAĞLI (eski ajan yok sayar) ──
    /** İstasyonun etkin personeli; `pinHash` argon2id özetidir, DÜZ PIN ağdan geçmez. KVKK: ad taşınır. */
    staff: z.array(agentMasterStaffSchema).optional(),
    /** Şirketin etkin, süresi geçmemiş kampanyaları (`@station/pricing-engine` şeması). */
    campaigns: z.array(campaignSchema).optional(),
    posTerminals: z.array(agentMasterPosTerminalSchema).optional(),
    /** OPEN ya da CLOSING vardiya ve üyeleri; yoksa null. SUNUCU durumudur ve `revision`'a GİRER. */
    openShift: agentMasterOpenShiftSchema.nullable().optional(),
    posSettings: agentMasterPosSettingsSchema.optional(),
    // ── Prompt 17 genişletmesi — hepsi İSTEĞE BAĞLI; `FLEET_LOYALTY` kapalıysa boş / `null` ──
    loyalty: agentMasterLoyaltyProgramSchema.nullable().optional(),
    loyaltyAccounts: z.array(agentMasterLoyaltyAccountSchema).optional(),
    coupons: z.array(agentMasterCouponSchema).optional(),
    subscriptions: z.array(agentMasterSubscriptionSchema).optional(),
    fleetVehicles: z.array(agentMasterFleetVehicleSchema).optional(),
    fleetDefaultPolicies: z.array(agentMasterFleetDefaultPolicySchema).optional(),
    fleetDrivers: z.array(agentMasterFleetDriverSchema).optional(),
    privacy: agentMasterPrivacySchema.optional(),
    // ── Prompt 18 — yıkama (`WASH_V1` yeteneği + `WASH` bayrağı; aksi hâlde null) ──
    wash: agentMasterWashSchema.nullable().optional(),
});
// ── §3 Giden olaylar ─────────────────────────────────────────────────────────
/**
 * Zarf. `payload` bilerek `unknown`'dur: tür başına şema `AGENT_EVENT_PAYLOAD_SCHEMAS` içindedir ve
 * KAYIT KAYIT çalıştırılır. Böylece tek bozuk / bilinmeyen kayıt partiyi düşürmez.
 */
export const agentSyncEventSchema = z.object({
    seq: z.number().int().positive().describe("Ajan ömrü boyunca MONOTON artan sıra numarası"),
    id: uuidSchema.describe("Olayın ajan tarafındaki kimliği (UUIDv7)"),
    type: z.string().min(1).max(60),
    deviceTime: timestampSchema.describe("Cihazın bildirdiği an (yoksa ajan saati)"),
    receivedAt: timestampSchema.describe("Ajanın olayı aldığı an"),
    payload: z.unknown(),
});
export const agentSyncRequestSchema = z.object({
    batchId: uuidSchema,
    sentAt: timestampSchema,
    events: z.array(agentSyncEventSchema).min(1).max(AGENT_SYNC_MAX_EVENTS),
    /**
     * Kurtarılamaz boşluk bildirimi (protokol §3 kural 6): sunucu yedekten dönüp daha önce onayladığı bir sırayı yeniden
     * istediğinde ajan onaylı olaylarını saklama süresi içinde YENİDEN gönderir; süre aşılmışsa elindeki ilk sıradan devam
     * ettiğini bununla söyler. Yalnızca partinin ilk `seq`'ine eşit ve sunucunun beklediğinden BÜYÜKSE geçerlidir (ileri atlama).
     */
    resumeFromSeq: z.number().int().positive().optional(),
});
export const agentSyncRejectionSchema = z.object({
    seq: z.number().int().positive(),
    id: uuidSchema,
    code: agentSyncRejectionCodeSchema,
    message: z.string(),
});
export const agentSyncWarningSchema = z.object({
    seq: z.number().int().positive(),
    code: z.string(),
    message: z.string(),
});
export const agentSyncResponseSchema = z.object({
    ackedSeq: z.number().int().nonnegative().describe("İşlenen en yüksek `seq`"),
    received: z.number().int().nonnegative(),
    accepted: z.number().int().nonnegative(),
    duplicates: z.number().int().nonnegative(),
    rejected: z.array(agentSyncRejectionSchema),
    warnings: z.array(agentSyncWarningSchema),
    serverTime: timestampSchema,
});
// ── §3.1 Olay yükleri (tür başına AYRI şema) ────────────────────────────────
export const agentNozzleTransactionPayloadSchema = z.object({
    nozzleId: uuidSchema,
    litres: quantitySchema,
    unitPrice: unitPriceSchema,
    amount: moneySchema.describe("Kuruş, KDV dâhil"),
    paymentType: fuelPaymentTypeSchema,
    agentTxId: z
        .string()
        .min(1)
        .max(100)
        .describe("`<adapterId>:<cihaz işlem no>` — idempotency doğal anahtarı"),
    isTest: z.boolean().optional(),
    testReturnedToTank: z.boolean().optional(),
    totalizerStart: quantitySchema.nullable().optional(),
    totalizerEnd: quantitySchema.nullable().optional(),
    fiscalReceiptRef: z.string().max(64).nullable().optional(),
    vehicleTagId: z.string().max(64).nullable().optional(),
});
export const agentTotalizerReadingPayloadSchema = z.object({
    nozzleId: uuidSchema,
    electronicL: quantitySchema,
    mechanicalL: quantitySchema.nullable().optional(),
});
export const agentTankReadingPayloadSchema = z.object({
    tankId: uuidSchema,
    levelMm: millimetreSchema,
    waterMm: millimetreSchema.nullable().optional(),
    temperatureC: temperatureSchema.nullable().optional(),
});
export const agentZReportVatLineSchema = z.object({
    rate: rateSchema.describe("KDV yüzdesi (ör. 20)"),
    base: nonNegativeMoneySchema.describe("Matrah (kuruş)"),
    vat: nonNegativeMoneySchema.describe("KDV tutarı (kuruş)"),
});
export const agentZReportTenderLineSchema = z.object({
    tenderType: z.string().regex(/^[A-Z][A-Z0-9_]{1,39}$/),
    amount: nonNegativeMoneySchema,
});
export const agentZReportPayloadSchema = z.object({
    fiscalDeviceSerial: z.string().trim().min(1).max(40),
    zNo: z.number().int().min(1).max(1_000_000),
    businessDate: localDateSchema,
    totalAmount: nonNegativeMoneySchema.describe("KDV dâhil gün toplamı (kuruş)"),
    vatBreakdown: z.array(agentZReportVatLineSchema).max(20).optional(),
    tenderBreakdown: z.array(agentZReportTenderLineSchema).max(20).optional(),
});
export const agentNozzleStatusPayloadSchema = z.object({
    nozzleId: uuidSchema,
    status: agentNozzleStatusSchema,
});
export const agentDeviceStatusPayloadSchema = z.object({
    adapterId: z.string().trim().min(1).max(60),
    deviceRef: z.string().trim().min(1).max(120),
    status: agentDeviceStatusSchema,
    detail: z.string().max(500).nullable().optional(),
});
export const agentDeviceAlarmPayloadSchema = z.object({
    adapterId: z.string().trim().min(1).max(60),
    deviceRef: z.string().trim().min(1).max(120),
    code: z.string().trim().min(1).max(60),
    severity: agentAlarmSeveritySchema,
    message: z.string().trim().min(1).max(1000),
});
export const agentWashTransactionPayloadSchema = z.object({
    bayRef: z.string().trim().min(1).max(60),
    programCode: z.string().trim().min(1).max(60),
    amount: nonNegativeMoneySchema,
    paymentType: fuelPaymentTypeSchema,
    washTxId: z.string().trim().min(1).max(100),
    durationSeconds: z.number().int().nonnegative(),
});
/**
 * Prompt 17 — TTS / plaka tanıma okuması (ADR 0017 §10, §15). KVKK: plaka ve etiket kişisel veri olabilir; sunucu
 * `vehicle_recognition_reads`'e yazar ve saklama süresi dolunca İMHA eder. `readAt` = olayın `deviceTime`'ı.
 */
export const agentVehicleReadPayloadSchema = z
    .object({
    readId: uuidSchema,
    source: z.enum(["TAG", "PLATE_CAMERA"]),
    tagId: z.string().trim().min(1).max(64).nullable().optional(),
    plate: z.string().trim().min(1).max(20).nullable().optional(),
    nozzleId: uuidSchema.nullable().optional(),
    agentTxId: z.string().trim().max(100).nullable().optional(),
    vehicleId: uuidSchema.nullable().optional(),
})
    .refine((value) => Boolean(value.tagId) || Boolean(value.plate), "Etiket ya da plakadan biri zorunludur");
/** Tür → yük şeması. Burada OLMAYAN tür `UNSUPPORTED_EVENT_TYPE` ile reddedilir (parti düşmez). */
export const AGENT_EVENT_PAYLOAD_SCHEMAS = {
    NozzleTransaction: agentNozzleTransactionPayloadSchema,
    TotalizerReading: agentTotalizerReadingPayloadSchema,
    TankReading: agentTankReadingPayloadSchema,
    ZReport: agentZReportPayloadSchema,
    NozzleStatus: agentNozzleStatusPayloadSchema,
    DeviceStatus: agentDeviceStatusPayloadSchema,
    DeviceAlarm: agentDeviceAlarmPayloadSchema,
    WashTransaction: agentWashTransactionPayloadSchema,
    // Prompt 15 — yerel kasa. Şemalar GEVŞEKTİR: satışı doğrulama hatasıyla reddetmek satış kaybıdır.
    PosSale: agentPosSalePayloadSchema,
    PosSaleVoid: agentPosSaleVoidPayloadSchema,
    PosSaleRetender: agentPosSaleRetenderPayloadSchema,
    // Prompt 17 — taşıt tanıma okuması.
    VehicleRead: agentVehicleReadPayloadSchema,
    // Prompt 18 — yıkama.
    WashCycle: agentWashCyclePayloadSchema,
    WashUnitStatus: agentWashUnitStatusPayloadSchema,
    WashActivation: agentWashActivationPayloadSchema,
    WashCreditIssued: agentWashCreditIssuedPayloadSchema,
};
// ── §4 Komutlar ──────────────────────────────────────────────────────────────
export const agentCommandQuerySchema = z.object({
    waitSeconds: z.coerce
        .number()
        .int()
        .min(0)
        .max(AGENT_COMMAND_MAX_WAIT_SECONDS)
        .default(25)
        .describe("Long-poll bekleme süresi (sn)"),
    limit: z.coerce.number().int().min(1).max(AGENT_COMMAND_MAX_LIMIT).default(20),
});
export const agentCommandSchema = z.object({
    id: uuidSchema,
    type: agentCommandTypeSchema,
    createdAt: timestampSchema,
    expiresAt: timestampSchema,
    attempt: z.number().int().positive(),
    payload: z.record(z.string(), z.unknown()),
});
export const agentCommandListSchema = z.object({
    commands: z.array(agentCommandSchema),
    serverTime: timestampSchema,
});
export const agentCommandAckSchema = z.object({ receivedAt: timestampSchema });
export const agentCommandResultSchema = z.object({
    status: z.enum(["SUCCEEDED", "FAILED"]),
    finishedAt: timestampSchema,
    detail: z.string().max(2000).optional(),
    output: z.record(z.string(), z.unknown()).optional(),
});
export const agentCommandAckResponseSchema = z.object({
    id: uuidSchema,
    status: z.string(),
    serverTime: timestampSchema,
});
// ── Komut yükleri (tür başına şema) ─────────────────────────────────────────
// Adlandırma notu: bu şemalar PATCH gövdesi DEĞİLDİR; `update…Schema` kalıbından kaçınılır
// (`common/patch.test.ts` o adı taşıyan her şemayı kısmi güncelleme sayar ve denetler).
export const agentUpdatePricesPayloadSchema = z.object({
    effectiveAt: timestampSchema,
    requestNo: z.string().nullable(),
    prices: z.array(z.object({ productId: uuidSchema, unitPrice: unitPriceSchema })),
});
export const agentUpdateConfigPayloadSchema = z.object({ config: agentConfigOverrideSchema });
export const agentRestartAdapterPayloadSchema = z.object({
    adapterId: z.string().trim().min(1).max(60),
});
export const agentUploadLogsPayloadSchema = z.object({
    sinceMinutes: z.number().int().min(1).max(10_080),
});
export const agentRefreshMasterDataPayloadSchema = z.object({});
/** Tür → yük şeması. Elle komut gönderirken sunucu bu şemayla doğrular. */
export const AGENT_COMMAND_PAYLOAD_SCHEMAS = {
    UPDATE_PRICES: agentUpdatePricesPayloadSchema,
    UPDATE_CONFIG: agentUpdateConfigPayloadSchema,
    RESTART_ADAPTER: agentRestartAdapterPayloadSchema,
    UPLOAD_LOGS: agentUploadLogsPayloadSchema,
    REFRESH_MASTER_DATA: agentRefreshMasterDataPayloadSchema,
    WASH_START: agentWashStartPayloadSchema,
};
// ── §5 Heartbeat ─────────────────────────────────────────────────────────────
export const agentAdapterHealthSchema = z.object({
    id: z.string().trim().min(1).max(60),
    kind: agentAdapterKindSchema,
    driver: z.string().trim().min(1).max(60),
    status: agentAdapterStatusSchema,
    detail: z.string().max(500).nullable().optional(),
    lastEventAt: timestampSchema.nullable().optional(),
});
/**
 * Ajan yetenekleri (Prompt 17, agent-protocol §2): ajan AYNI kümeyi ana veri isteğinde (`X-Agent-Capabilities` başlığı,
 * virgülle) ve heartbeat gövdesinde bildirir; sunucu ana veriyi ve `revision`'ı bu kümeye göre üretir (iki yol aynı özeti
 * vermezse ajan ana veriyi durmadan yeniden çekerdi). Bilinmeyen yetenek yok sayılır.
 *  - `CAMPAIGNS_V3` → kupon kampanyası (`requiresCoupon`) ve çapraz koşul (`crossCondition`) anlaşılır. Bildirmeyen eski
 *    ajana bu kampanyalar GÖNDERİLMEZ (eski POS alanları atar ve kampanyayı koşulsuz uygulardı).
 *  - `WASH_V1` (Prompt 18) → ana verinin `wash` bölümü, yıkama olayları ve `WASH_START` komutu anlaşılır. Bildirmeyen ajana
 *    `wash: null` gider ve `WASH_START` üretilmez (aktivasyon "ajan desteklemiyor" ile başarısız olur).
 *  - `EV_V1` (Prompt 19) → `GET /agent/v1/ev/state`, `POST /agent/v1/ev/remote-start` ve satış satırındaki `evSessionId`
 *    anlaşılır. Bildirmeyen ajana şarj uçları `404` döner.
 */
export const AGENT_CAPABILITIES = ["CAMPAIGNS_V3", "WASH_V1", "EV_V1"];
export const agentCapabilitiesSchema = z.array(z.string().trim().min(1).max(40)).max(32);
/** `X-Agent-Capabilities` başlığını (virgülle ayrılmış) bilinen yetenek kümesine çevirir. */
export function parseAgentCapabilities(value) {
    const items = typeof value === "string" ? value.split(",") : (value ?? []);
    const known = new Set(AGENT_CAPABILITIES);
    return new Set(items.map((item) => item.trim()).filter((item) => known.has(item)));
}
export const agentHeartbeatRequestSchema = z.object({
    sentAt: timestampSchema,
    version: agentVersionSchema,
    commit: z.string().max(40).nullable().optional(),
    os: agentOsSchema,
    arch: agentArchSchema,
    hostname: z.string().trim().min(1).max(120),
    uptimeSeconds: z.number().int().nonnegative(),
    queueDepth: z.number().int().nonnegative(),
    deadLetterCount: z.number().int().nonnegative(),
    oldestPendingAt: timestampSchema.nullable(),
    lastSyncAt: timestampSchema.nullable(),
    lastAckedSeq: z.number().int().nonnegative(),
    masterDataRevision: z.string().max(64).nullable(),
    updateChannel: agentUpdateChannelSchema,
    adapters: z.array(agentAdapterHealthSchema).max(50),
    disk: z.object({ freeBytes: z.number().int().nonnegative(), totalBytes: z.number().int().nonnegative() }),
    memory: z.object({ rssBytes: z.number().int().nonnegative() }),
    /** Prompt 17: ana veri isteğindeki `X-Agent-Capabilities` ile AYNI küme. */
    capabilities: agentCapabilitiesSchema.optional(),
});
export const agentUpdateHintSchema = z.object({
    version: z.string(),
    channel: agentUpdateChannelSchema,
});
export const agentHeartbeatResponseSchema = z.object({
    serverTime: timestampSchema,
    clockSkewMs: z.number().int().describe("Sunucu alım anı − `sentAt` (ms)"),
    pendingCommands: z.number().int().nonnegative(),
    masterDataRevision: z.string(),
    update: agentUpdateHintSchema.nullable(),
});
// ── §6 Güncelleme ────────────────────────────────────────────────────────────
export const agentManifestQuerySchema = z.object({
    channel: agentUpdateChannelSchema.default("stable"),
    os: agentOsSchema,
    arch: agentArchSchema,
});
export const agentManifestResponseSchema = z.object({
    manifest: z.string().describe("base64: manifest JSON baytları"),
    signature: z.string().describe("base64: ed25519(manifest baytları)"),
});
/** İmzalanan manifest JSON'unun içeriği (sunucu AYRIŞTIRIR ve doğrular; baytları DEĞİŞTİRMEZ). */
export const agentManifestDocumentSchema = z.object({
    version: z.string().regex(/^\d+\.\d+\.\d+(-[0-9A-Za-z.-]+)?$/, "Sürüm semver olmalıdır (ör. 1.1.0)"),
    channel: agentUpdateChannelSchema,
    os: agentOsSchema,
    arch: agentArchSchema,
    url: z.url({ protocol: /^https?$/ }),
    sha256: z.string().regex(/^[0-9a-f]{64}$/),
    size: z.number().int().positive(),
    releasedAt: timestampSchema,
    minVersion: z
        .string()
        .regex(/^\d+\.\d+\.\d+(-[0-9A-Za-z.-]+)?$/)
        .nullable()
        .optional(),
    notes: z.string().max(4000).nullable().optional(),
});
// ── §4 Günlük yükleme ────────────────────────────────────────────────────────
export const agentLogUploadResponseSchema = z.object({
    id: uuidSchema,
    sizeBytes: z.number().int().nonnegative(),
    receivedAt: timestampSchema,
});
//# sourceMappingURL=protocol.js.map